import React, { useState, useEffect } from 'react';
import { 
  ArrowRight,
  Lightbulb,
  Shield,
  Brain,
  Workflow,
  Building2,
  CheckCircle,
  Target,
  Clock,
  Users,
  Settings,
  Lock,
  Cloud,
  Database,
  BarChart,
  Network,
  Code,
  FileText,
  ServerCrash,
  Wallet,
  LineChart
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Styles d'animation
const styles = `
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }

  @keyframes pulse {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }

  .floating {
    animation: float 6s ease-in-out infinite;
  }

  .pulsing {
    animation: pulse 2s ease-in-out infinite;
  }
`;

// Composant d'arrière-plan animé
const AnimatedBackground = () => (
  <div className="fixed inset-0 -z-10">
    {/* Gradient radial */}
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(1,125,126,0.15),transparent_50%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center_left,rgba(0,169,157,0.1),transparent_50%)]" />
    
    {/* Bordure supérieure brillante */}
    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-500/50 to-transparent" />

    {/* Grille et particules */}
    <div className="absolute inset-0 opacity-30">
      <div className="absolute inset-0"
        style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '4rem 4rem',
          maskImage: 'radial-gradient(circle at 50% 50%, black, transparent 80%)'
        }}
      />
    </div>

    {/* Particules flottantes */}
    <div className="absolute inset-0 overflow-hidden">
      {[...Array(50)].map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-[#017D7E]/20"
          style={{
            width: Math.random() * 4 + 'px',
            height: Math.random() * 4 + 'px',
            top: Math.random() * 100 + '%',
            left: Math.random() * 100 + '%',
            animation: `float ${Math.random() * 6 + 4}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 4}s`
          }}
        />
      ))}
    </div>
  </div>
);

// Composants utilitaires
const StatBox = ({ number, text }) => (
  <div className="group bg-white/5 rounded-lg p-4 border border-white/10 w-full md:w-auto
                hover:border-green-500/50 transition-all duration-300">
    <div className="text-xl md:text-2xl font-bold text-[#017D7E] mb-1 group-hover:scale-110 
                   transition-transform duration-300">
      {number}
    </div>
    <div className="text-sm text-white/70">{text}</div>
  </div>
);
// Composant ServiceCard
const ServiceCard = ({ icon: Icon, title, description, features, benefits, methodologies, certifications, examples }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="group bg-white/5 rounded-xl border border-white/10 transition-all duration-300
                  hover:border-green-500/50">
      {/* En-tête de la carte */}
      <div className="p-6 md:p-8">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 rounded-lg bg-[#017D7E]/10 flex-shrink-0">
            <Icon className="w-6 h-6 text-[#017D7E]" />
          </div>
          <div>
            <h3 className="text-xl font-bold mb-2">{title}</h3>
            <p className="text-white/70">{description}</p>
          </div>
        </div>

        {/* Liste des caractéristiques principales */}
        <div className="space-y-2 mb-6">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start gap-2 text-white/70">
              <CheckCircle className="w-4 h-4 mt-1 text-[#017D7E] flex-shrink-0" />
              <span>{feature}</span>
            </div>
          ))}
        </div>

        {/* Bouton d'expansion */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-[#017D7E] hover:text-[#00A99D] transition-colors flex items-center gap-2"
        >
          <span>{isExpanded ? 'Voir moins' : 'En savoir plus'}</span>
          <ArrowRight className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`} />
        </button>
      </div>

      {/* Contenu détaillé */}
      <div className={`border-t border-white/10 transition-all duration-500 overflow-hidden
                    ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="p-6 md:p-8 space-y-6">
          {/* Bénéfices */}
          <div>
            <h4 className="text-lg font-semibold mb-3">Bénéfices Clés</h4>
            <ul className="space-y-2">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-2 text-white/70">
                  <Target className="w-4 h-4 mt-1 text-[#017D7E] flex-shrink-0" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Méthodologies */}
          <div>
            <h4 className="text-lg font-semibold mb-3">Méthodologies</h4>
            <ul className="space-y-2">
              {methodologies.map((methodology, index) => (
                <li key={index} className="flex items-start gap-2 text-white/70">
                  <Settings className="w-4 h-4 mt-1 text-[#017D7E] flex-shrink-0" />
                  <span>{methodology}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Certifications */}
          <div>
            <h4 className="text-lg font-semibold mb-3">Certifications</h4>
            <ul className="space-y-2">
              {certifications.map((certification, index) => (
                <li key={index} className="flex items-start gap-2 text-white/70">
                  <FileText className="w-4 h-4 mt-1 text-[#017D7E] flex-shrink-0" />
                  <span>{certification}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Exemples de réalisations */}
          <div>
            <h4 className="text-lg font-semibold mb-3">Exemples de Réalisations</h4>
            <ul className="space-y-4">
              {examples.map((example, index) => (
                <li key={index} className="bg-white/5 rounded-lg p-4">
                  <h5 className="font-semibold mb-2">{example.title}</h5>
                  <p className="text-white/70 text-sm">{example.description}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {example.results.map((result, idx) => (
                      <span key={idx} className="text-xs bg-[#017D7E]/10 text-[#017D7E] px-2 py-1 rounded-full">
                        {result}
                      </span>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
// Données des services
const services = [
    {
      title: "Stratégie & Transformation",
      description: "Accompagnement stratégique pour votre transformation digitale",
      icon: Lightbulb,
      features: [
        "Stratégie digitale et innovation",
        "Transformation des systèmes d'information",
        "Optimisation des processus métiers",
        "Architecture d'entreprise",
        "Feuille de route technologique"
      ],
      benefits: [
        "Accélération de la transformation digitale",
        "Réduction des coûts opérationnels",
        "Amélioration de l'expérience client",
        "Innovation accélérée"
      ],
      methodologies: [
        "Design Thinking",
        "Business Model Canvas",
        "Enterprise Architecture Framework",
        "Value Stream Mapping"
      ],
      certifications: [
        "TOGAF",
        "ArchiMate",
        "Digital Strategy Certification",
        "Change Management"
      ],
      examples: [
        {
          title: "Transformation Digitale Banque Retail",
          description: "Refonte complète de l'architecture SI et des processus",
          results: [
            "-40% coûts IT",
            "+60% satisfaction client",
            "TTM -50%"
          ]
        }
      ]
    },
    {
      title: "Conformité & Risque",
      description: "Excellence en conformité et gestion des risques",
      icon: Shield,
      features: [
        "Conformité réglementaire (RGPD, DSP2, Bâle)",
        "Gestion des risques IT",
        "Cybersécurité et résilience",
        "Protection des données",
        "Audit et certification"
      ],
      benefits: [
        "Conformité garantie",
        "Risques maîtrisés",
        "Protection renforcée",
        "Confiance accrue"
      ],
      methodologies: [
        "ISO 31000",
        "NIST Framework",
        "COBIT",
        "ITIL"
      ],
      certifications: [
        "ISO 27001",
        "CISA",
        "CISSP",
        "CRISC"
      ],
      examples: [
        {
          title: "Programme Conformité RGPD",
          description: "Mise en conformité complète d'une institution financière",
          results: [
            "100% conforme",
            "Risques -75%",
            "0 incident"
          ]
        }
      ]
    },
    {
      title: "Innovation & Technologies",
      description: "Excellence technologique et innovation continue",
      icon: Brain,
      features: [
        "Intelligence artificielle & Machine Learning",
        "Blockchain & DLT",
        "Cloud & DevOps",
        "Open Banking & APIs",
        "Architectures modernes"
      ],
      benefits: [
        "Innovation accélérée",
        "Performance optimisée",
        "Scalabilité améliorée",
        "Compétitivité renforcée"
      ],
      methodologies: [
        "Agile Innovation",
        "Design Sprint",
        "Lean Startup",
        "MVP Approach"
      ],
      certifications: [
        "Cloud Certifications",
        "Blockchain Certifications",
        "AI/ML Certifications",
        "API Design"
      ],
      examples: [
        {
          title: "Plateforme IA Trading",
          description: "Développement d'algorithmes ML pour le trading automatisé",
          results: [
            "+25% performance",
            "-35% risques",
            "ROI 300%"
          ]
        }
      ]
    },
    {
      title: "Delivery & Méthodes",
      description: "Excellence opérationnelle et méthodologique",
      icon: Workflow,
      features: [
        "Agilité à l'échelle",
        "DevSecOps",
        "Quality Engineering",
        "Organisation des équipes",
        "Gestion de projet"
      ],
      benefits: [
        "Delivery accéléré",
        "Qualité améliorée",
        "Efficacité optimisée",
        "Collaboration renforcée"
      ],
      methodologies: [
        "SAFe",
        "Scrum@Scale",
        "DevOps",
        "Kanban"
      ],
      certifications: [
        "PMP",
        "SAFe",
        "Scrum",
        "PRINCE2"
      ],
      examples: [
        {
          title: "Transformation Agile",
          description: "Mise en place de l'agilité à l'échelle pour 200+ personnes",
          results: [
            "TTM -60%",
            "Productivité +40%",
            "Satisfaction +45%"
          ]
        }
      ]
    },
    {
      title: "Conseil Métier Finance",
      description: "Expertise sectorielle et métier approfondie",
      icon: Building2,
      features: [
        "Core Banking",
        "Paiements & Transactions",
        "Marchés de capitaux",
        "Gestion d'actifs",
        "Crédit & Risque"
      ],
      benefits: [
        "Excellence opérationnelle",
        "Performance optimisée",
        "Conformité garantie",
        "Innovation ciblée"
      ],
      methodologies: [
        "Business Process Management",
        "Lean Six Sigma",
        "Value Chain Analysis",
        "Customer Journey Mapping"
      ],
      certifications: [
        "CFA",
        "FRM",
        "CAIA",
        "Banking Certifications"
      ],
      examples: [
        {
          title: "Modernisation Core Banking",
          description: "Transformation du système core banking d'une banque majeure",
          results: [
            "Coûts -45%",
            "Performance +70%",
            "Agilité x3"
          ]
        }
      ]
    }
];
// Composant principal Consulting
const Consulting = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState({});
  const [activeTab, setActiveTab] = useState(0);

  // Effet pour les styles d'animation
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
    return () => styleSheet.remove();
  }, []);

  // Effet pour l'animation au scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting
          }));
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach(
      el => observer.observe(el)
    );

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen text-white">
      <AnimatedBackground />

      {/* Hero Section */}
      <div className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-green-500/20 
                        to-[#017D7E]/20 text-[#017D7E] mb-8 group hover:scale-105 transition-all"
                 data-animate
                 id="hero-badge">
              <div className="relative z-10">Excellence en Conseil</div>
              <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-[#017D7E]/10 
                          rounded-full animate-pulse" />
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 md:mb-8 leading-tight"
                data-animate
                id="hero-title">
              Expertise Conseil
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
                Pour la Finance de Demain
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/70 mb-8 md:mb-12">
              De la stratégie à l'exécution, nos experts vous accompagnent dans votre transformation
              avec une approche sur mesure et une expertise pointue du secteur financier.
            </p>

          

            <button
              onClick={() => navigate('/contact')}
              className="group px-6 md:px-8 py-3 md:py-4 rounded-lg bg-gradient-to-r from-green-500 
                      to-[#017D7E] hover:from-green-600 hover:to-emerald-600 text-white font-medium 
                      transition-all flex items-center gap-2 mx-auto">
              <span className="relative z-10 flex items-center gap-2">
                Discuter de votre projet
                <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Navigation des services */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {services.map((service, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTab(index)}
                  className={`px-4 py-2 rounded-lg transition-all ${
                    activeTab === index
                      ? 'bg-[#017D7E] text-white'
                      : 'bg-white/5 text-white/70 hover:bg-white/10'
                  }`}
                >
                  <span className="text-sm md:text-base">{service.title}</span>
                </button>
              ))}
            </div>

            {/* Contenu des services */}
            <div className="grid grid-cols-1 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className={`transition-all duration-500 ${
                    activeTab === index ? 'opacity-100' : 'opacity-0 hidden'
                  }`}
                >
                  <ServiceCard {...service} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

  
      

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Prêt à Transformer Votre Organisation ?
            </h2>
            <p className="text-lg md:text-xl text-white/70 mb-8">
              Nos experts sont à votre disposition pour échanger sur vos enjeux
              et vous accompagner dans votre transformation.
            </p>
            
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/contact')}
                className="px-8 py-4 rounded-lg bg-gradient-to-r from-green-500 to-[#017D7E]
                         hover:from-green-600 hover:to-emerald-600 text-white font-medium transition-all">
                Contacter nos Experts
              </button>
              
              <button
                onClick={() => navigate('/portfolio')}
                className="px-8 py-4 rounded-lg border border-green-500/50 hover:bg-[#017D7E]/10
                         text-white font-medium transition-all">
                Découvrir nos Solutions
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Consulting;